import React, { useState, useEffect, useRef } from 'react'
import { Button, Table, Row, Col, Modal } from 'antd'
import { RocketOutlined, SafetyOutlined, SettingOutlined, DatabaseOutlined, ClusterOutlined, ProductOutlined } from '@ant-design/icons'
import IconFont from '@/components/IconFonts'
import Card3D, { ProductImage3D } from '@/components/Card3D'
import { useRouter } from 'next/router'
import type { GetStaticProps } from 'next'
import { ORG_ID, absUrl, breadcrumbLd, SeoProps } from '@/utils/seo'
import TypewriterText from '@/components/TypewriterText'

const LingtiDealerPage = () => {
  const [visibleScenarios, setVisibleScenarios] = useState<Set<number>>(new Set())
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set())
  const [heroVisible, setHeroVisible] = useState(false)
  const [wechatModalVisible, setWechatModalVisible] = useState(false)
  const [kefuModalVisible, setKefuModalVisible] = useState(false)
  const scenarioRefs = useRef<Array<HTMLDivElement | null>>([])
  const sectionRefs = useRef<Array<HTMLDivElement | null>>([])
  const heroRef = useRef<HTMLDivElement>(null)

  const router = useRouter()
  useEffect(() => {
    const scenarioObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement
            const index = parseInt(target.dataset.index || '0', 10)
            setVisibleScenarios((prev) => new Set(Array.from(prev).concat(index)))
          }
        })
      },
      {
        threshold: 0.3,
        rootMargin: '-50px 0px',
      }
    )

    scenarioRefs.current.forEach((ref) => {
      if (ref) scenarioObserver.observe(ref)
    })

    return () => scenarioObserver.disconnect()
  }, [])

  useEffect(() => {
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement
            const sectionId = target.dataset.section
            if (sectionId) {
              setVisibleSections((prev) => new Set(Array.from(prev).concat(sectionId)))
            }
          }
        })
      },
      {
        threshold: 0.15,
        rootMargin: '-100px 0px',
      }
    )

    sectionRefs.current.forEach((ref) => {
      if (ref) sectionObserver.observe(ref)
    })

    return () => sectionObserver.disconnect()
  }, [])

  useEffect(() => {
    const heroObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHeroVisible(true)
          }
        })
      },
      {
        threshold: 0.3,
        rootMargin: '-50px 0px',
      }
    )

    if (heroRef.current) {
      heroObserver.observe(heroRef.current)
    }

    return () => heroObserver.disconnect()
  }, [])

  const techFeatures = [
    {
      icon: <SettingOutlined />,
      title: '智能操控系统',
      description: '支持Web端、微信小程序、iOS、Android等多平台远程操控，界面简洁直观',
    },
    {
      icon: <RocketOutlined />,
      title: 'AI视觉识别',
      description: '内置高性能AI视觉识别模块，精准识别印刷字体、艺术字体、自定义图案',
    },
    {
      icon: <ProductOutlined />,
      title: '模块化设计',
      description: '发牌槽数支持3至108槽灵活扩展，高强度结构设计，便于维护',
    },
    {
      icon: <SafetyOutlined />,
      title: '安全公平性',
      description: '使用AI大模型生成牌库，内置牌力均衡算法，保障比赛公正性',
    },
    {
      icon: <ClusterOutlined />,
      title: '集群协同',
      description: '支持多达百台设备并发协同发牌，云边协同架构适合大型赛事',
    },
    {
      icon: <DatabaseOutlined />,
      title: '系统联动',
      description: '与灵缇赛事系统深度集成，支持实时同步、牌局复盘、数据统计',
    },
  ]

  const scenarios = [
    {
      id: 0,
      title: '竞技比赛',
      description: '适用于地区性和全国性竞技牌类比赛，发牌机采用纯机械全自动化发牌，无需人为干预，确保比赛的公正性。',
      features: ['通过AI模型训练及AI对抗学习', '内置海量均衡牌力牌谱库', '支持牌库远程更新'],
      image: '/fushiji/poker2.png',
    },
    {
      id: 1,
      title: '娱乐社交',
      description: '棋牌俱乐部、社区活动中心、亲友聚会的理想选择，增加游戏乐趣和公平性。',
      features: ['可发放指定炸数的娱乐用牌', '可调节牌力的发牌专用牌库，可定向控制牌力', '可自定义牌库功能，通过可视化的牌库编辑工具', '生成俱乐部专用训练牌'],
      image: '/fushiji/poker1.png',
    },
    {
      id: 2,
      title: '培训教学',
      description: '为棋牌教学与牌技培训提供标准化发牌服务，帮助学员掌握标准牌局规律。',
      features: ['支持特定牌型的专项训练模式', '集成灵缇赛事系统牌例回放功能', '一键发出专属教学牌型'],
      image: '/fushiji/teach.png',
    },
  ]

  const gameTypes = [
    {
      name: '掼蛋',
      level: '热门',
      gradient: 'from-red-500 to-pink-500',
      iconBg: 'bg-gradient-to-r from-red-500 to-pink-500',
      icon: '掼',
    },
    {
      name: '桥牌',
      level: '竞技',
      gradient: 'from-blue-500 to-indigo-500',
      iconBg: 'bg-gradient-to-r from-blue-500 to-indigo-500',
      icon: '桥',
    },
    {
      name: '斗地主',
      level: '经典',
      gradient: 'from-orange-500 to-red-500',
      iconBg: 'bg-gradient-to-r from-orange-500 to-red-500',
      icon: '斗',
    },
    {
      name: '拱猪',
      level: '趣味',
      gradient: 'from-green-500 to-emerald-500',
      iconBg: 'bg-gradient-to-r from-green-500 to-emerald-500',
      icon: '拱',
    },
    {
      name: '地方扑克',
      level: '定制',
      gradient: 'from-purple-500 to-pink-500',
      iconBg: 'bg-gradient-to-r from-purple-500 to-pink-500',
      icon: '地',
    },
  ]

  const specifications = [
    { param: '重量', value: '3-5千克，轻巧便携' },
    { param: '控制方式', value: 'Web、小程序、iOS、Android' },
    { param: '发牌槽扩展', value: '3至108槽' },
    { param: '算法支持', value: '随机生成、牌力均衡算法' },
    { param: '联动系统', value: '灵缇赛事系统（可选集成）' },
    { param: '产地', value: '中国·苏州' },
  ]

  const milestones = [
    {
      id: '01',
      year: '2023',
      stage: '诞生',
      title: '灵缇复式发牌机',
      subtitle: '又称"灵缇智能博弈终端" 一代机问世',
      award: '荣获 2023 年 苏州市人工智能科学进步奖 二等奖',
      isHighlight: false,
    },
    {
      id: '02',
      year: '2024',
      stage: '发展',
      title: '灵缇智能博弈终端 二代机 亮相',
      subtitle: '',
      award: '荣获 2024 年 苏州市人工智能科学进步奖 一等奖',
      isHighlight: false,
    },
    {
      id: '03',
      year: '2025.7',
      stage: '现状',
      title: '灵缇智能博弈终端 三代机',
      subtitle: '',
      award: '正式问世量产',
      isHighlight: true,
    },
  ]

  const tableColumns = [
    {
      title: '参数项目',
      dataIndex: 'param',
      key: 'param',
      className: 'font-semibold text-white bg-gray-800/50',
    },
    {
      title: '规格说明',
      dataIndex: 'value',
      key: 'value',
      className: 'text-gray-300 bg-gray-800/50',
    },
  ]

  return (
    <div className="min-h-screen text-gray-800">
      <style>
        {`
          /* Apple-style Typography System */
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
          
          * {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }

          /* Apple-style Easing Curves */
          :root {
            --apple-easing: cubic-bezier(0.25, 0.1, 0.25, 1);
            --apple-smooth: cubic-bezier(0.16, 1, 0.3, 1);
            --apple-spring: cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }

          /* Scenario Section */
          .scenario-title {
            opacity: 0;
            transform: translateY(-60px) scale(0.9);
            transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
          }

          .scenario-title.scenario-card-visible {
            opacity: 1;
            transform: translateY(0) scale(1);
          }

          .scenario-card-left {
            opacity: 0;
            transform: translateX(-100px) translateY(-50px) rotate(-45deg) scale(0.8);
            transition: all 1s cubic-bezier(0.16, 1, 0.3, 1);
          }

          .scenario-card-right {
            opacity: 0;
            transform: translateX(100px) translateY(-50px) rotate(45deg) scale(0.8);
            transition: all 1s cubic-bezier(0.16, 1, 0.3, 1);
          }

          .scenario-card-visible {
            opacity: 1;
            transform: translateX(0) translateY(0) rotate(0deg) scale(1);
          }

          .scenario-card-left.scenario-card-visible {
            transition-delay: 0.1s;
          }

          .scenario-card-right.scenario-card-visible {
            transition-delay: 0.2s;
          }

          .scenario-row:nth-child(2) .scenario-card-left.scenario-card-visible {
            transition-delay: 0.3s;
          }

          .scenario-row:nth-child(2) .scenario-card-right.scenario-card-visible {
            transition-delay: 0.4s;
          }

          .scenario-row:nth-child(3) .scenario-card-left.scenario-card-visible {
            transition-delay: 0.5s;
          }

          .scenario-row:nth-child(3) .scenario-card-right.scenario-card-visible {
            transition-delay: 0.6s;
          }

          .section-fade-up {
            opacity: 0;
            transform: translateY(20px);
            transition: all 0.6s ease-out;
          }

          .section-fade-up.section-visible {
            opacity: 1;
            transform: translateY(0);
          }

          .ant-card {
            background: rgba(255, 255, 255, 0.85) !important;
            border: 1px solid rgba(255, 255, 255, 0.3) !important;
            border-radius: 16px !important;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
            transition: all 0.3s ease-out !important;
            will-change: transform, box-shadow;
          }

          .ant-card:hover {
            background: rgba(255, 255, 255, 0.95) !important;
            border-color: rgba(59, 130, 246, 0.4) !important;
            box-shadow: 0 8px 30px rgba(59, 130, 246, 0.15) !important;
            transform: translateY(-4px) !important;
          }

          .custom-button-primary {
            background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%) !important;
            border: none !important;
            border-radius: 12px !important;
            box-shadow: 0 4px 16px rgba(0, 122, 255, 0.3) !important;
            transition: all 0.3s ease-out !important;
            font-weight: 600 !important;
            letter-spacing: -0.01em !important;
            will-change: transform, box-shadow;
          }

          .custom-button-primary:hover {
            background: linear-gradient(135deg, #0056CC 0%, #4A47A3 100%) !important;
            box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4) !important;
            transform: translateY(-2px) !important;
          }

          .custom-button-secondary {
            background: rgba(255, 255, 255, 0.9) !important;
            border: 1px solid rgba(0, 0, 0, 0.1) !important;
            border-radius: 12px !important;
            color: #1d1d1f !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
            transition: all 0.3s ease-out !important;
            font-weight: 600 !important;
            letter-spacing: -0.01em !important;
            will-change: transform, box-shadow;
          }

          .custom-button-secondary:hover {
            background: rgba(248, 248, 248, 1) !important;
            border-color: rgba(0, 0, 0, 0.2) !important;
            color: #1d1d1f !important;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
            transform: translateY(-2px) !important;
          }

          @keyframes float-simple {
            0%, 100% { 
              transform: translateY(0); 
            }
            50% { 
              transform: translateY(-4px); 
            }
          }

          .animate-float {
            animation: float-simple 4s ease-in-out infinite;
          }

          /* Apple-style Typography Enhancements */
          .apple-title {
            font-weight: 700;
            letter-spacing: -0.03em;
            line-height: 1.1;
            background: linear-gradient(135deg, #1d1d1f 0%, #86868b 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .apple-subtitle {
            font-weight: 400;
            letter-spacing: -0.01em;
            line-height: 1.4;
            color: #86868b;
          }

          .ant-table {
            background: transparent !important;
            border-radius: 16px !important;
            overflow: hidden !important;
          }

          .ant-table-thead > tr > th {
            background: rgba(248, 250, 252, 0.6) !important;
            color: #1d1d1f !important;
            border: none !important;
            font-weight: 600 !important;
            letter-spacing: -0.01em !important;
            padding: 16px 24px !important;
          }

          .ant-table-tbody > tr > td {
            background: transparent !important;
            color: #1d1d1f !important;
            border: none !important;
            padding: 16px 24px !important;
            border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
          }

          .ant-table-tbody > tr:hover > td {
            background: rgba(0, 0, 0, 0.02) !important;
          }

          .ant-table-tbody > tr:last-child > td {
            border-bottom: none !important;
          }

          @media (max-width: 1024px) {
            .scenario-card-left,
            .scenario-card-right {
              transform: translateY(30px) scale(0.95);
              filter: blur(4px);
            }

            .scenario-card-visible {
              transform: translateY(0) scale(1);
              filter: blur(0px);
            }
          }

          .hero-glass {
            background: rgba(255, 255, 255, 0.2);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 16px;
          }

          .hero-card-wrapper {
            opacity: 0;
            transform: translateY(40px) scale(0.6) rotate(30deg);
            transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
          }

          .hero-card-wrapper.hero-visible {
            opacity: 1;
            transform: translateY(0) scale(1) rotate(0deg);
          }

          .grid .hero-card-wrapper:nth-child(1) { 
            transform: translateY(40px) translateX(-40px) scale(0.6) rotate(-30deg);
            transition-delay: 0.3s; 
          }
          .grid .hero-card-wrapper:nth-child(1).hero-visible { 
            transform: translateY(0) translateX(0) scale(1) rotate(0deg); 
          }

          .grid .hero-card-wrapper:nth-child(2) { 
            transform: translateY(40px) translateX(40px) scale(0.6) rotate(30deg);
            transition-delay: 0.4s; 
          }
          .grid .hero-card-wrapper:nth-child(2).hero-visible { 
            transform: translateY(0) translateX(0) scale(1) rotate(0deg); 
          }

          .grid .hero-card-wrapper:nth-child(3) { 
            transform: translateY(40px) translateX(-40px) scale(0.6) rotate(-30deg);
            transition-delay: 0.5s; 
          }
          .grid .hero-card-wrapper:nth-child(3).hero-visible { 
            transform: translateY(0) translateX(0) scale(1) rotate(0deg); 
          }

          .grid .hero-card-wrapper:nth-child(4) { 
            transform: translateY(40px) translateX(40px) scale(0.6) rotate(30deg);
            transition-delay: 0.6s; 
          }
          .grid .hero-card-wrapper:nth-child(4).hero-visible { 
            transform: translateY(0) translateX(0) scale(1) rotate(0deg); 
          }

          .flex.hero-card-wrapper { 
            transform: translateY(40px) scale(0.8);
            transition-delay: 0.8s; 
          }
          .flex.hero-card-wrapper.hero-visible { 
            transform: translateY(0) scale(1); 
          }

          .hero-product-image {
            opacity: 0;
            transform: translateX(-120px) translateY(-60px) rotate(-45deg) scale(0.8);
            transition: all 1.2s cubic-bezier(0.16, 1, 0.3, 1);
          }

          .hero-product-image.hero-visible {
            opacity: 1;
            transform: translateX(0) translateY(0) rotate(0deg) scale(1);
          }

          .hero-content {
            opacity: 0;
            transform: translateX(120px) translateY(-60px) rotate(45deg) scale(0.8);
            transition: all 1.2s cubic-bezier(0.16, 1, 0.3, 1);
          }

          .hero-content.hero-visible {
            opacity: 1;
            transform: translateX(0) translateY(0) rotate(0deg) scale(1);
          }

          .ant-btn:focus,
          .custom-button-primary:focus,
          .custom-button-secondary:focus {
            outline: 2px solid #007AFF !important;
            outline-offset: 2px !important;
          }

          @keyframes shine {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }

          .animate-shine {
            animation: shine 0.6s ease-out;
          }

          .group:hover .animate-shine {
            animation: shine 0.6s ease-out;
          }
        `}
      </style>

      <div className="fixed inset-0 -z-50">
        <img src="/fushiji/bg1.png" alt="背景" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-white/20" />
      </div>

      {/* Hero Section */}
      <section ref={heroRef} className="relative flex min-h-screen items-center justify-center overflow-hidden px-6 py-12">
        <div className="relative z-20 mx-auto max-w-7xl text-center">
          <div className="mb-12">
            <div className="hero-glass animate-float mb-6 inline-block px-6 py-3">
              <span className="text-xs font-medium tracking-wider text-gray-600">🇨🇳 中国·苏州制造 · AI智能 · 物联网技术 · 云边协同</span>
            </div>
            <h1 className="apple-title mb-6 text-3xl leading-none md:text-4xl lg:text-5xl">
              灵缇三代
              <br />
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">复式发牌机</span>
            </h1>
            <TypewriterText text="世界首款融合AI、IoT与云边协同的智能博弈终端" speed={100} delay={100} className="mx-auto mb-8 max-w-4xl text-base lg:text-2xl" />
          </div>

          <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className={`hero-product-image ${heroVisible ? 'hero-visible' : ''}`}>
              <div className="mx-auto w-full max-w-sm sm:max-w-md lg:max-w-lg">
                <ProductImage3D
                  src="https://assets.ruilisi.com/89bc3bd6-0f4a-4790-b461-f80361b6c85e-image.png"
                  alt="灵缇三代复式发牌机"
                  width="100%"
                  height="auto"
                  className="h-auto w-full"
                  intensity="normal"
                />
              </div>
            </div>

            <div className={`hero-content ${heroVisible ? 'hero-visible' : ''}`}>
              <div className="space-y-6">
                <div>
                  <h2 className="apple-title mb-4 text-2xl lg:text-3xl">
                    重新定义
                    <br />
                    智能发牌标准
                  </h2>
                  <p className="apple-subtitle text-base lg:text-lg">突破传统界限，创造智能竞技新时代</p>
                </div>

                <div className="grid grid-cols-1 gap-4 gap-x-8 lg:grid-cols-2">
                  {[
                    { icon: 'icon-ai', title: 'AI识别', desc: '精准识别各种牌面图案' },
                    { icon: 'icon-safety', title: '公平算法', desc: '牌力均衡，保障比赛公正' },
                    { icon: 'icon-devices', title: '模块化设计', desc: '3-108槽灵活扩展' },
                    { icon: 'icon-router', title: '集群协同', desc: '支持百台设备并发' },
                  ].map((item, index) => (
                    <div key={+index} className={`hero-card-wrapper ${heroVisible ? 'hero-visible' : ''}`}>
                      <Card3D className="bg-white/85 p-6" intensity="strong">
                        <div className="flex flex-col items-center space-y-3 text-center">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 shadow-lg transition-transform duration-300">
                            <IconFont icon={item.icon} className="text-lg text-white" />
                          </div>
                          <h3 className="font-semibold text-gray-800">{item.title}</h3>
                          <p className="text-sm leading-relaxed text-gray-600">{item.desc}</p>
                        </div>
                      </Card3D>
                    </div>
                  ))}
                </div>

                <div className={`hero-card-wrapper ${heroVisible ? 'hero-visible' : ''} flex flex-col justify-center gap-4 pt-6 sm:flex-row`}>
                  <Button
                    type="primary"
                    size="large"
                    className="custom-button-primary h-auto px-10 py-4 text-lg"
                    onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    立即订购
                  </Button>
                  <Button
                    size="large"
                    className="custom-button-secondary h-auto px-10 py-4 text-lg"
                    onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    了解详情
                  </Button>
                  <Button size="large" className="custom-button-secondary h-auto px-10 py-4 text-lg" onClick={() => router.push('/fushiji/faq')}>
                    百问百答
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Features Section */}
      <section
        id="features"
        className={`section-fade-up relative flex min-h-screen items-center py-20 ${visibleSections.has('features') ? 'section-visible' : ''}`}
        ref={(el) => {
          sectionRefs.current[0] = el as HTMLDivElement
        }}
        data-section="features"
      >
        <div className="mx-auto w-full max-w-7xl px-6">
          <div className="mb-16 text-center">
            <h2 className="apple-title mb-6 text-3xl md:text-4xl lg:text-5xl">技术革新</h2>
            <p className="apple-subtitle mx-auto max-w-3xl text-lg md:text-xl">
              融合前沿科技，重新定义智能发牌标准。
              <br />
              每一项技术都经过精心打磨，只为创造完美体验。
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {techFeatures.map((feature, index) => (
              <Card3D
                key={+index}
                className="bg-white/85 group relative h-full overflow-hidden border border-white/30 p-6"
                intensity="normal"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative z-10 flex h-full flex-col">
                  <div className="mb-6 flex items-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 shadow-xl transition-all duration-500">
                      <span className="text-xl text-white">{feature.icon}</span>
                    </div>
                  </div>

                  <h3 className="mb-3 text-xl font-bold leading-tight text-gray-900">{feature.title}</h3>

                  <div className="mb-4 h-1 w-16 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 opacity-60 transition-all duration-500 group-hover:w-24 group-hover:opacity-100" />

                  <p className="flex-1 text-base leading-relaxed text-gray-900">{feature.description}</p>
                </div>
              </Card3D>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Videos Section */}
      <section
        className={`section-fade-up relative py-32 ${visibleSections.has('demo') ? 'section-visible' : ''}`}
        ref={(el) => {
          sectionRefs.current[1] = el as HTMLDivElement
        }}
        data-section="demo"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-24 text-center">
            <h2 className="apple-title mb-8 text-3xl md:text-4xl lg:text-5xl">实机演示</h2>
            <p className="apple-subtitle mx-auto max-w-3xl text-xl md:text-2xl">
              亲眼见证灵缇三代的智能发牌过程。
              <br />
              每一个动作都诠释着精密工程的魅力。
            </p>
          </div>

          <div className="grid gap-12 lg:grid-cols-2">
            <div className="group relative">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-400 to-purple-500 opacity-10 blur-xl transition-opacity duration-300 group-hover:opacity-20" />
              <Card3D className="bg-white/85 overflow-hidden" intensity="subtle">
                <video
                  src="https://assets.ruilisi.com/0ebdd066-bb0d-480e-8e23-254bf7d6837f-2346_1753262497.mp4"
                  controls
                  className="w-full rounded-2xl shadow-2xl"
                  poster="/fushiji/ai2.png"
                >
                  <track kind="captions" src="" label="Chinese" />
                  您的浏览器不支持视频播放
                </video>
                <div className="p-8">
                  <h3 className="mb-3 text-2xl font-bold text-gray-900">AI识别牌谱演示</h3>
                  <p className="text-lg leading-relaxed text-gray-600">对于用户提出的任何特异牌型，都可在 10~60分钟内完成专项训练，从而精准发牌。</p>
                </div>
              </Card3D>
            </div>

            <div className="group relative">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-400 to-pink-500 opacity-10 blur-xl transition-opacity duration-300 group-hover:opacity-20" />
              <Card3D className="bg-white/85 overflow-hidden" intensity="subtle">
                <video
                  src="https://assets.ruilisi.com/2941d627-8211-4de2-8ddc-0e1789e7e602-2337_1753157403.mp4"
                  controls
                  className="w-full rounded-2xl shadow-2xl"
                  poster="/fushiji/fapaiji.png"
                >
                  <track kind="captions" src="" label="Chinese" />
                  您的浏览器不支持视频播放
                </video>
                <div className="p-8">
                  <h3 className="mb-3 text-2xl font-bold text-gray-900">发牌流程演示</h3>
                  <p className="text-lg leading-relaxed text-gray-600">
                    完整展示自动发牌操作流程，
                    <br />
                    流畅自然，一气呵成
                  </p>
                </div>
              </Card3D>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios Section */}
      <section id="scenarios" className="relative py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className={`scenario-title ${visibleScenarios.has(0) ? 'scenario-card-visible' : ''} mb-16 text-center`}>
            <h2 className="mb-6 text-4xl font-bold text-gray-800 md:text-5xl">适用场景</h2>
            <p className="mx-auto max-w-2xl text-xl text-gray-600">竞技比赛、培训教学、娱乐社交，满足多元化应用需求</p>
          </div>

          <div className="space-y-20">
            {scenarios.map((scenario, index) => (
              <Row
                key={`scenario-${scenario.id}`}
                ref={(el) => {
                  scenarioRefs.current[index] = el
                }}
                data-index={index}
                gutter={[32, 32]}
                align="middle"
                className={`scenario-row ${index % 2 === 1 ? 'flex-row-reverse' : ''}`}
              >
                <Col xs={24} lg={12}>
                  <div className={`${index % 2 === 0 ? 'scenario-card-left' : 'scenario-card-right'} ${visibleScenarios.has(index) ? 'scenario-card-visible' : ''}`}>
                    <Card3D className="bg-white/80" intensity="normal">
                      <div className="p-8">
                        <div className="mb-6 flex items-center gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-blue-500 text-lg font-bold text-white">
                            {index + 1}
                          </div>
                          <h3 className="text-2xl font-bold text-gray-800">{scenario.title}</h3>
                        </div>

                        <p className="mb-4 text-lg leading-relaxed text-gray-600">{scenario.description}</p>

                        {scenario.features && (
                          <div className="mb-6 space-y-2">
                            {scenario.features.map((feature) => (
                              <div key={feature} className="flex items-start gap-3">
                                <div className="mt-1.5 h-2 w-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" />
                                <span className="text-sm text-gray-700">{feature}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card3D>
                  </div>
                </Col>

                <Col xs={24} lg={12}>
                  <div className={`${index % 2 === 0 ? 'scenario-card-right' : 'scenario-card-left'} ${visibleScenarios.has(index) ? 'scenario-card-visible' : ''}`}>
                    <ProductImage3D src={scenario.image} alt={scenario.title} width="100%" height={320} className="rounded-2xl object-cover shadow-2xl" intensity="normal" />
                  </div>
                </Col>
              </Row>
            ))}
          </div>
        </div>
      </section>

      {/* Game Types Section */}
      <section
        className={`section-fade-up relative py-20 ${visibleSections.has('games') ? 'section-visible' : ''}`}
        ref={(el) => {
          sectionRefs.current[2] = el as HTMLDivElement
        }}
        data-section="games"
      >
        <div className="mx-auto max-w-4xl px-6">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold md:text-5xl">支持玩法</h2>
            <p className="text-xl text-gray-600">覆盖主流扑克牌类游戏，满足不同用户需求</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {gameTypes.map((game, index) => (
              <Card3D
                key={`game-${game.name}`}
                className="group relative overflow-hidden border border-white/30 bg-white/90"
                intensity="normal"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="p-6">
                  <div className="mb-4 flex items-center gap-4">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl shadow-lg ${game.iconBg}`}>
                      <span className="text-lg font-bold text-white">{game.icon}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900">{game.name}</h3>
                      <div className="mt-1 flex items-center gap-2">
                        <div className="flex items-center">
                          <div className={`h-2 w-16 rounded-full bg-gradient-to-r ${game.gradient} shadow-sm`} />
                        </div>
                        <span className="text-xs font-medium text-gray-500">{game.level}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card3D>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Card3D className="border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50" intensity="subtle">
              <div className="p-6">
                <div className="flex items-center justify-center gap-3">
                  <IconFont icon="iconaddition" className="text-xl text-blue-600" />
                  <span className="text-lg font-semibold text-blue-900">支持更多地方性自定义扑克规则</span>
                </div>
                <p className="mt-2 text-sm text-blue-700">可根据需求定制开发专属玩法</p>
              </div>
            </Card3D>
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section
        className={`section-fade-up relative py-20 ${visibleSections.has('specs') ? 'section-visible' : ''}`}
        ref={(el) => {
          sectionRefs.current[3] = el as HTMLDivElement
        }}
        data-section="specs"
      >
        <div className="mx-auto max-w-4xl px-6">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold text-gray-800 md:text-5xl">性能参数</h2>
            <p className="text-xl text-gray-600">专业级硬件配置，满足各种应用场景</p>
          </div>

          <Card3D className="border border-gray-200 bg-white/80 hover:border-blue-500/50" intensity="subtle">
            <div className="p-8">
              <Table
                columns={tableColumns}
                dataSource={specifications.map((spec) => ({
                  ...spec,
                  key: `spec-${spec.param}`,
                }))}
                pagination={false}
                className="w-full"
              />
            </div>
          </Card3D>
        </div>
      </section>

      {/* Product History Section */}
      <section
        className={`section-fade-up relative py-20 ${visibleSections.has('history') ? 'section-visible' : ''}`}
        ref={(el) => {
          sectionRefs.current[4] = el as HTMLDivElement
        }}
        data-section="history"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-20 text-center">
            <h2 className="apple-title mb-6 text-4xl font-bold text-gray-800 md:text-5xl">产品发展史</h2>
            <p className="text-xl text-gray-600">从创新理念到行业领先，见证智能发牌技术的evolution</p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 top-0 h-full w-1 -translate-x-1/2 bg-gradient-to-b from-teal-500 via-teal-600 to-teal-700" />

            <div className="absolute left-1/2 top-0 flex h-full -translate-x-1/2 flex-col justify-between">
              {milestones.map((milestone) => (
                <div key={milestone.id} className="relative">
                  <div className={`h-4 w-4 rounded-full border-4 border-white shadow-lg ${milestone.isHighlight ? 'bg-red-500' : 'bg-teal-500'}`} />
                </div>
              ))}
            </div>

            <div className="space-y-32">
              {milestones.map((milestone, index) => (
                <div key={milestone.id} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className="flex-1">
                    <Card3D className="mx-auto max-w-md border border-white/30 bg-white/90" intensity="normal">
                      <div className="p-8">
                        <div className="mb-4 flex items-center gap-4">
                          <div
                            className={`flex h-12 w-12 items-center justify-center rounded-full text-lg font-bold text-white ${
                              milestone.isHighlight ? 'bg-gradient-to-r from-red-500 to-red-600' : 'bg-gradient-to-r from-teal-500 to-teal-600'
                            }`}
                          >
                            {milestone.id}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-500">{milestone.stage}</div>
                            <div className="text-2xl font-bold text-gray-800">{milestone.year}</div>
                          </div>
                        </div>

                        <h3 className="mb-2 text-xl font-bold text-gray-900">{milestone.title}</h3>

                        {milestone.subtitle && <p className="mb-4 text-sm text-gray-600">{milestone.subtitle}</p>}

                        {milestone.award && (
                          <div className={`rounded-lg p-3 ${milestone.isHighlight ? 'border border-red-200 bg-red-50' : 'border border-orange-200 bg-orange-50'}`}>
                            <p className={`text-sm font-medium ${milestone.isHighlight ? 'text-red-700' : 'text-orange-700'}`}>{milestone.award}</p>
                          </div>
                        )}
                      </div>
                    </Card3D>
                  </div>

                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        className={`section-fade-up relative py-20 ${visibleSections.has('pricing') ? 'section-visible' : ''}`}
        ref={(el) => {
          sectionRefs.current[5] = el as HTMLDivElement
        }}
        data-section="pricing"
      >
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-16 text-center">
            <h2 className="apple-title mb-8 text-3xl md:text-4xl lg:text-5xl">超值定价</h2>
            <p className="apple-subtitle mx-auto max-w-3xl text-xl md:text-2xl">
              专业设备，超值价格。
              <br />
              助力您的智能化升级之路。
            </p>
          </div>

          <div className="relative mx-auto max-w-3xl">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 opacity-20 blur-3xl" />
            <Card3D
              className="to-white/85 relative overflow-hidden rounded-3xl border-[1.5px] border-white/30 bg-gradient-to-br from-white/95 shadow-2xl outline outline-[2px] outline-[rgba(120,119,198,0.6)] backdrop-blur-md"
              intensity="subtle"
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-80 transition-opacity duration-300"
                style={{
                  background: `radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(120,119,198,0.3), rgba(255,255,255,0) 50%)`,
                }}
              />

              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-purple-50/30 opacity-80" />

              <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-b from-black/5 via-transparent to-white/5 opacity-30" />

              <div className="absolute inset-0 rounded-3xl border border-white/20" />

              <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_60%_at_50%_-20%,rgba(59,130,246,0.15),rgba(255,255,255,0))]" />

              <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_120%,rgba(147,51,234,0.15),rgba(255,255,255,0))]" />

              <div className="absolute inset-0 z-[-1] overflow-hidden">
                <div
                  className="absolute inset-0 transition-all duration-500"
                  style={{
                    background: `radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(120,119,198,0.4), rgba(255,255,255,0) 70%)`,
                  }}
                />

                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_120%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_80%_80%_at_50%_120%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />

                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_60%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_60%_60%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />

                <div className="via-blue-400/15 absolute inset-0 bg-gradient-to-r from-transparent to-transparent opacity-70" />

                <div className="absolute inset-0 rounded-3xl border border-[rgba(120,119,198,0.3)] opacity-50" />
              </div>

              <div className="relative z-10 p-8">
                <div className="mb-8 text-center">
                  <div className="group relative mb-6 inline-flex items-center overflow-hidden rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 px-6 py-3 shadow-lg">
                    <span className="group-hover:animate-shine absolute inset-0 h-full w-full -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                    <span className="relative z-10 text-sm font-semibold tracking-wide text-white">🎉 限时特惠</span>
                  </div>

                  <div className="mb-6">
                    <div className="apple-title mb-2 text-5xl text-gray-900 drop-shadow-sm md:text-6xl">¥9,999</div>
                    <div className="apple-subtitle text-xl">人民币</div>
                  </div>

                  <div className="group relative inline-flex items-center overflow-hidden rounded-2xl border border-green-200 bg-gradient-to-r from-green-100 to-emerald-100 px-6 py-3">
                    <span className="group-hover:animate-shine absolute inset-0 h-full w-full -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                    <span className="relative z-10 text-sm font-semibold text-green-800">赠送灵缇赛事系统VIP半年 + 一年维保服务</span>
                  </div>
                </div>

                <div className="mx-auto max-w-2xl">
                  <h3 className="mb-6 text-center text-2xl font-bold text-gray-900">购买包含</h3>
                  <div className="space-y-3">
                    {[
                      '灵缇三代复式发牌机设备一台',
                      '灵缇赛事系统至尊VIP半年使用权',
                      '一年设备维保服务（含云端支持）',
                      '完整技术文档与操作指南',
                      '7×24小时技术支持服务',
                      '软件更新与功能升级服务',
                    ].map((item, index) => (
                      <div key={+index} className="group flex items-start gap-4">
                        <div className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-blue-500 shadow-md transition-all duration-300 group-hover:scale-110">
                          <IconFont icon="iconcheck" className="text-xs text-white" />
                        </div>
                        <span className="text-lg leading-relaxed text-gray-700 transition-colors duration-300 group-hover:text-gray-900">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                  <Button
                    type="primary"
                    size="large"
                    className="group relative h-auto flex-1 overflow-hidden border-0 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 px-12 py-6 text-xl text-white shadow-xl backdrop-blur-sm transition-all duration-300 hover:from-blue-700 hover:via-purple-700 hover:to-blue-800"
                    onClick={() => router.push('/contact')}
                  >
                    <span className="absolute inset-0 z-0 h-full w-full bg-black/10" />

                    <span className="group-hover:animate-shine absolute inset-0 h-full w-full -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent" />

                    <span className="relative z-10 text-base font-medium tracking-wide">立即订购</span>
                  </Button>
                </div>
              </div>
            </Card3D>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        className={`section-fade-up py-32 ${visibleSections.has('contact') ? 'section-visible' : ''}`}
        ref={(el) => {
          sectionRefs.current[6] = el as HTMLDivElement
        }}
        data-section="contact"
        id="contact"
      >
        <div className="mx-auto max-w-7xl px-6 text-center">
          <div className="mb-24">
            <h2 className="apple-title mb-8 text-3xl md:text-4xl lg:text-5xl">联系我们</h2>
            <p className="apple-subtitle mx-auto max-w-3xl text-xl md:text-2xl">
              随时为您提供专业服务。
              <br />
              让我们一起开启智能竞技新时代。
            </p>
          </div>

          <div className="mb-20 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                image: '/lingti-gd-logo.png',
                title: '官方网站',
                subtitle: 'hy.lingti.com',
                color: 'from-blue-600 to-indigo-600',
                onClick: () => router.push('/'),
              },
              { icon: 'icon-wechat', title: '微信客服', subtitle: '微信号：lingtij', color: 'from-green-600 to-emerald-600', onClick: () => setWechatModalVisible(true) },
              { icon: 'icon-kefu', title: '7×24客服', subtitle: '手机号：15599036698', color: 'from-purple-600 to-pink-600', onClick: () => setKefuModalVisible(true) },
              { icon: 'icon-address', title: '中国苏州', subtitle: '自主研发制造', color: 'from-orange-600 to-red-600' },
            ].map((contact, index) => (
              <Card3D
                key={`contact-${contact.icon || contact.image}`}
                className={`bg-white/85 p-8 ${contact.onClick ? 'cursor-pointer hover:shadow-2xl' : ''}`}
                intensity="normal"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={contact.onClick}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-blue-100/20 opacity-0 transition-opacity duration-700 group-hover:opacity-100" />

                <div className="relative z-10 text-center">
                  <div className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-r ${contact.color} shadow-xl transition-all duration-500`}>
                    {contact.image ? (
                      <img src={contact.image} alt={contact.title} className="h-8 w-8 object-contain" />
                    ) : (
                      <IconFont icon={contact.icon} className="text-3xl text-white" />
                    )}
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-gray-900">{contact.title}</h3>
                  <p className="text-base font-medium text-gray-600">{contact.subtitle}</p>
                </div>
              </Card3D>
            ))}
          </div>

          <div className="relative mx-auto max-w-4xl">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 opacity-10 blur-2xl" />

            <Card3D className="bg-white/85 p-12" intensity="normal">
              <h3 className="apple-title mb-6 text-4xl md:text-5xl">灵缇三代复式发牌机</h3>
              <p className="apple-subtitle text-xl md:text-2xl">
                不止是一台发牌机，
                <br className="hidden md:block" />
                更是连接未来智能竞技生态的入口
              </p>
            </Card3D>
          </div>
        </div>
      </section>

      <Modal title="微信客服" open={wechatModalVisible} onCancel={() => setWechatModalVisible(false)} footer={null} width={400} centered>
        <div className="p-6 text-center">
          <div className="mb-4">
            <img src="/fushiji/vx.png" alt="微信二维码" className="mx-auto h-72 w-72 rounded-lg object-contain shadow-lg" />
          </div>
          <div className="mb-4">
            <h4 className="mb-2 text-lg font-semibold text-gray-800">扫码添加微信客服</h4>
            <p className="mb-1 text-gray-600">
              微信号：<span className="font-mono text-green-600">lingtij</span>
            </p>
          </div>
        </div>
      </Modal>

      <Modal title="7×24客服" open={kefuModalVisible} onCancel={() => setKefuModalVisible(false)} footer={null} width={450} centered>
        <div className="p-6">
          <div className="mb-6 text-center">
            <h4 className="mb-2 text-lg font-semibold text-gray-800">专业技术支持团队</h4>
            <p className="mb-6 text-sm text-gray-500">全天候为您提供专业服务</p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center rounded-lg border border-purple-100 bg-gradient-to-r from-purple-50 to-pink-50 p-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-pink-600 font-semibold text-white">冯</div>
              <div className="ml-4 flex-1">
                <h5 className="font-semibold text-gray-800">冯先生</h5>
                <p className="text-gray-600">
                  手机号：<span className="font-mono text-purple-600">15599036698</span>
                </p>
              </div>
              <a href="tel:15599036698" className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white transition-colors hover:bg-brand-dark">
                <IconFont icon="icon-mobile" className="text-sm" />
              </a>
            </div>

            <div className="flex items-center rounded-lg border border-purple-100 bg-gradient-to-r from-purple-50 to-pink-50 p-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-pink-600 font-semibold text-white">臧</div>
              <div className="ml-4 flex-1">
                <h5 className="font-semibold text-gray-800">臧经理</h5>
                <p className="text-gray-600">
                  手机号：<span className="font-mono text-purple-600">15370036553</span>
                </p>
              </div>
              <a href="tel:15370036553" className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white transition-colors hover:bg-brand-dark">
                <IconFont icon="icon-mobile" className="text-sm" />
              </a>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">服务时间：7×24小时全天候服务</p>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const seo: SeoProps = {
    title: '灵缇三代复式发牌机 · 掼蛋/桥牌/双升/斗地主智能发牌机 | 灵缇互娱',
    description:
      '灵缇三代复式发牌机:AI 视觉识别牌面、牌力均衡算法、3–108 槽模块化扩展、百台集群协同,与灵缇赛事系统联动,支持掼蛋、桥牌、双升、斗地主等复式比赛。苏州自主研发,获苏州市人工智能科学进步奖。',
    keywords: '复式发牌机,智能发牌机,掼蛋发牌机,桥牌发牌机,自动发牌机,扑克发牌机,复式赛,灵缇发牌机,灵缇智能博弈终端',
    path: '/fushiji',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: '灵缇三代复式发牌机',
        alternateName: '灵缇智能博弈终端',
        brand: { '@type': 'Brand', name: '灵缇互娱' },
        manufacturer: { '@id': ORG_ID },
        image: [absUrl('/lingti-gd-logo.png')],
        description:
          'AI 视觉识别牌面、牌力均衡算法、3–108 槽模块化扩展、支持多达百台设备集群协同发牌,与灵缇赛事系统深度集成,适用于掼蛋、桥牌、双升、斗地主等竞技比赛、培训教学与娱乐社交。',
        award: ['2023 年苏州市人工智能科学进步奖二等奖', '2024 年苏州市人工智能科学进步奖一等奖'],
        countryOfOrigin: '中国',
        offers: { '@type': 'Offer', price: '9999', priceCurrency: 'CNY', availability: 'https://schema.org/InStock', url: absUrl('/fushiji') },
      },
      breadcrumbLd([
        ['首页', '/'],
        ['复式发牌机', '/fushiji'],
      ]),
    ],
  }
  return { props: { seo } }
}

export default LingtiDealerPage
